import { useNavigate, useParams } from "react-router-dom";
import { FieldErrors, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { confirmDialog } from "primereact/confirmdialog";

import { ItemFormRule, ItemFormSchema } from "@factory/form/form.rule";
import { useFormDefaultItem } from "@factory/form/form-default-item.hook";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { ActionEnum } from "@factory/model/actions.enum";
import { getFormErrorMessage } from "@core/utility/get-error-message";
import {
  useQyUpdateItem,
  useQyGetItemById,
  useQyDeleteItem,
} from "@factory/query/item.query";
import { GetResponseItemModel } from "@factory/model/query-model-item";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { ButtonAction } from "@shared/ui/header-button/button-action";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import { useUserIdentityContext } from "@core/services/user-identity.context";

export function useEditItem() {
  // LOCAL DECLARATION
  const { getUserRole } = useUserIdentityContext();
  const { formatDate } = useDateTimeContext();
  const { getDefault } = useFormDefaultItem();
  const navigate = useNavigate();
  const { mapUpdateItem, mapDeleteItem } = useFormApiMapper();
  const { showError, showSuccess } = useNotificationContext();
  const { itemId } = useParams();
  const actions: ButtonAction[] = [
    { label: "Update", type: "split", value: ActionEnum.Update },
    { label: "Delete", type: "more", value: ActionEnum.Delete },
  ];

  // QUERY DECLARATION
  // API GETBYID Item
  const handleGetApiSuccess = (data: GetResponseItemModel) => {
    if (!data || data.data?.length === 0) {
      return;
    }

    const responseData = data.data?.[0];

    setValue("name", responseData?.name || "");
    setValue("description", responseData?.description || "");
    setValue("amount", responseData?.amount);
    setValue("tenant", responseData?.tenant || "");
    setValue("duration", responseData?.duration || 0);
  };
  const {
    data: itemResponse,
    isLoading,
    isError: itemError,
  } = useQyGetItemById(itemId || "", handleGetApiSuccess);
  const itemData = itemResponse?.data?.[0];

  // API UPDATE Item
  const handleUpdateApiSuccess = () => {
    showSuccess("Item updated successfully");
  };
  const { mutate: updateItem, isLoading: isEditLoading } = useQyUpdateItem(
    handleUpdateApiSuccess
  );

  // API DELETE Item
  const handleDeleteApiSuccess = () => {
    handleBack();
    showSuccess("Item deleted successfully");
  };
  const { mutate: deleteItem } = useQyDeleteItem(handleDeleteApiSuccess);

  // LOCAL FUNCTIONS
  const formMethod = useForm<ItemFormSchema>({
    defaultValues: getDefault(itemData),
    resolver: zodResolver(ItemFormRule),
  });
  const { handleSubmit, setValue, watch, getValues } = formMethod;
  const handleValidate = (form: ItemFormSchema) => {
    if (!itemData || !itemData.code) {
      throw new Error("No item data");
    }
    const { tenant } = getUserRole();
    const updatedForm = {
      ...form,
      tenant,
    };

    const formData = mapUpdateItem(updatedForm, itemData.code);
    updateItem(formData);
  };
  const handleValidateError = (err: FieldErrors<ItemFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };
  const handleBack = () => {
    navigate("../");
  };
  const accept = () => {
    if (!itemData || !itemData.code) {
      throw new Error("No item data found");
    }

    const form = mapDeleteItem(itemData.code);
    deleteItem(form);
  };
  const handleAction = (action: ButtonAction) => {
    switch (action.value) {
      case ActionEnum.Update:
        handleSubmit(handleValidate, handleValidateError)();
        break;

      case ActionEnum.Delete:
        confirmDialog({
          message: "Are you sure you want to delete this record?",
          header: "Delete Confirmation",
          icon: "pi pi-info-circle",
          defaultFocus: "reject",
          acceptClassName: "p-button-danger",
          accept,
        });
        break;
    }
  };

  return {
    itemId,
    itemData,
    isLoading,
    itemError,
    formMethod,
    actions,
    isEditLoading,
    handleSubmit,
    setValue,
    watch,
    getValues,
    handleBack,
    handleAction,
  };
}
